<template>
  <div>
    <div
      v-if="!fechaInicialMaestra || !fechaFinalMaestra"
      class="alert alert-warning"
    >
      <i class="fas fa-exclamation-triangle mr-2"></i>
      <strong>Advertencia:</strong> Debe establecer las fechas inicial y final
      en el formulario principal antes de seleccionar fechas en el calendario.
    </div>
    <div v-else class="alert alert-info">
      <i class="fas fa-info-circle mr-2"></i>
      <strong>Rango permitido:</strong> Solo puede seleccionar fechas entre
      <strong>{{ formatearFecha(fechaInicialMaestra) }}</strong> y
      <strong>{{ formatearFecha(fechaFinalMaestra) }}</strong> (Fechas Maestras)
    </div>
    <div class="row">
      <div class="col-md-8">
        <div class="form-group">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <label class="mb-0">Seleccionar Fechas en el Calendario</label>
            <button
              type="button"
              class="btn btn-sm btn-primary"
              @click="seleccionarTodosLosDiasHabilitados()"
              :disabled="
                esEstadoDefinitivo || !fechaInicialMaestra || !fechaFinalMaestra
              "
              title="Seleccionar todos los días habilitados del rango"
            >
              <i class="fas fa-check-double mr-1"></i>
              Seleccionar Todos
            </button>
          </div>
          <div style="max-width: 600px">
            <div class="mb-2 text-center">
              <button
                type="button"
                class="btn btn-sm btn-secondary"
                @click="mesAnterior()"
                :disabled="esEstadoDefinitivo"
              >
                <i class="fas fa-chevron-left"></i>
              </button>
              <h5 class="d-inline-block mx-3 mb-0">
                {{ getNombreMes(calendarioMes) }} {{ calendarioAnio }}
              </h5>
              <button
                type="button"
                class="btn btn-sm btn-secondary"
                @click="mesSiguiente()"
                :disabled="esEstadoDefinitivo"
              >
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
            <div
              style="
                display: grid;
                grid-template-columns: repeat(7, 1fr);
                gap: 2px;
                background-color: #dee2e6;
                padding: 2px;
              "
            >
              <div
                v-for="dia in diasSemana"
                :key="dia"
                style="
                  background-color: #343a40;
                  color: white;
                  padding: 8px;
                  text-align: center;
                  font-weight: bold;
                "
              >
                {{ dia }}
              </div>
              <div
                v-for="(dia, index) in diasCalendario"
                :key="index"
                @click="
                  dia.habilitado && !esEstadoDefinitivo
                    ? toggleFecha(dia.fecha)
                    : null
                "
                :style="{
                  padding: '10px',
                  textAlign: 'center',
                  cursor:
                    dia.habilitado && !esEstadoDefinitivo
                      ? 'pointer'
                      : 'not-allowed',
                  backgroundColor: dia.seleccionado
                    ? '#007bff'
                    : !dia.habilitado
                    ? '#e9ecef'
                    : dia.esHoy
                    ? '#ffc107'
                    : dia.esDelMes
                    ? 'white'
                    : '#f8f9fa',
                  color: dia.seleccionado
                    ? 'white'
                    : !dia.habilitado
                    ? '#adb5bd'
                    : dia.esDelMes
                    ? '#6c757d'
                    : 'black',
                  fontWeight: dia.esHoy ? 'bold' : 'normal',
                  border: dia.esHoy ? '2px solid #ffc107' : '1px solid #dee2e6',
                  opacity: dia.habilitado ? '1' : '0.5',
                }"
                :title="
                  !dia.habilitado
                    ? 'Esta fecha está fuera del rango permitido'
                    : ''
                "
              >
                {{ dia.numero }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="form-group">
          <label>Fechas Seleccionadas</label>
          <div
            class="selected-dates-list"
            style="max-height: 200px; overflow-y: auto"
          >
            <div v-if="fechasSeleccionadas.length === 0" class="text-muted">
              <small>No hay fechas seleccionadas</small>
            </div>
            <div v-else>
              <div
                v-for="(fecha, index) in fechasSeleccionadasOrdenadas"
                :key="index"
                class="badge badge-primary mr-1 mb-1"
                :style="
                  esEstadoDefinitivo
                    ? 'cursor: not-allowed; opacity: 0.6;'
                    : 'cursor: pointer;'
                "
                @click="!esEstadoDefinitivo ? eliminarFecha(fecha) : null"
              >
                {{ formatearFecha(fecha) }}
                <i class="fas fa-times ml-1" v-if="!esEstadoDefinitivo"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="form-group">
          <label>Total de Días</label>
          <input
            type="number"
            class="form-control form-control-sm"
            :value="fechasSeleccionadas.length"
            readonly
            :class="fechasSeleccionadas.length > 0 ? 'is-valid' : 'is-invalid'"
          />
        </div>
        <div class="form-group">
          <button
            type="button"
            class="btn btn-sm btn-danger"
            @click="limpiarFechasSeleccionadas()"
            v-if="fechasSeleccionadas.length > 0"
            :disabled="esEstadoDefinitivo"
          >
            <i class="fas fa-trash mr-1"></i>
            Limpiar Fechas
          </button>
          <button
            type="button"
            class="btn btn-sm btn-success"
            @click="aplicarFechasCalendario()"
            v-if="fechasSeleccionadas.length > 0"
            :disabled="esEstadoDefinitivo"
          >
            <i class="fas fa-check mr-1"></i>
            Aplicar Fechas
          </button>
        </div>
      </div>
    </div>

    <!-- Selección de Bloque y Sitio para Calendario -->
    <div
      class="card mt-3"
      v-if="fechasSeleccionadas.length > 0 && bloques.length > 0"
    >
      <div class="card-header bg-success">
        <h3 class="card-title text-white">
          <i class="fas fa-map-marked-alt mr-2"></i>
          Asignar Volúmenes por Fecha
        </h3>
      </div>
      <div class="card-body">
        <div class="row mb-3">
          <div class="form-group col-md-6">
            <label>Seleccionar Bloque</label>
            <select
              class="form-control form-control-sm"
              v-model="bloqueSeleccionado"
              :disabled="esEstadoDefinitivo"
              @change="sitiosSeleccionados = []"
            >
              <option value="">Seleccione un bloque...</option>
              <option
                v-for="bloque in bloques"
                :key="bloque.id"
                :value="bloque.id"
              >
                {{ bloque.nombre }}
              </option>
            </select>
          </div>
          <div class="form-group col-md-6">
            <label>Seleccionar Sitios (Múltiple)</label>
            <select
              class="form-control form-control-sm"
              v-model="sitiosSeleccionados"
              multiple
              :disabled="!bloqueSeleccionado || esEstadoDefinitivo"
              size="5"
            >
              <option
                v-for="sitio in sitiosDelBloqueSeleccionado"
                :key="sitio.id"
                :value="sitio.id"
              >
                {{ sitio.nombre }}
              </option>
            </select>
            <small class="form-text text-muted">
              Mantenga presionada la tecla Ctrl (Cmd en Mac) para seleccionar
              múltiples sitios
            </small>
          </div>
        </div>

        <!-- Tablas de Volúmenes por Fecha para cada sitio -->
        <div v-if="sitiosSeleccionados.length > 0">
          <div
            v-for="sitioId in sitiosSeleccionados"
            :key="sitioId"
            class="mb-4"
          >
            <div class="card">
              <div class="card-header bg-info">
                <h5 class="card-title text-white mb-0">
                  <i class="fas fa-map-marker-alt mr-2"></i>
                  {{ obtenerNombreSitio(sitioId) }}
                </h5>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table
                    class="table table-bordered table-striped table-hover table-sm"
                  >
                    <thead class="bg-dark text-white">
                      <tr>
                        <th class="text-center">Fecha</th>
                        <th class="text-center">
                          Volumen por Día
                          <button
                            type="button"
                            class="btn btn-xs btn-info ml-2"
                            @click="duplicarVolumenes(sitioId)"
                            :disabled="esEstadoDefinitivo"
                            title="Duplicar el volumen del primer día a todos los días"
                          >
                            <i class="fas fa-copy"></i>
                          </button>
                        </th>
                        <th class="text-center">
                          Resultado (Vol/Día ÷ VolSitio)
                        </th>
                        <th class="text-center">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="fecha in fechasSeleccionadasOrdenadas"
                        :key="fecha"
                      >
                        <td class="align-middle">
                          <strong>{{ formatearFecha(fecha) }}</strong>
                        </td>
                        <td class="align-middle">
                          <div class="input-group input-group-sm">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              class="form-control form-control-sm text-right"
                              :value="getVolumenCalendario(sitioId, fecha)"
                              @input="
                                setVolumenCalendario(
                                  sitioId,
                                  fecha,
                                  $event.target.value
                                )
                              "
                              :disabled="esEstadoDefinitivo"
                              placeholder="0.00"
                            />
                            <div class="input-group-append">
                              <button
                                type="button"
                                class="btn btn-sm btn-outline-secondary"
                                @click="duplicarVolumenAFechas(sitioId, fecha)"
                                :disabled="esEstadoDefinitivo"
                                title="Copiar este volumen a todas las fechas"
                              >
                                <i class="fas fa-copy"></i>
                              </button>
                            </div>
                          </div>
                        </td>
                        <td class="align-middle text-right">
                          <strong class="text-success">
                            {{
                              formatNumber(
                                getResultadoCalendario(sitioId, fecha)
                              )
                            }}
                          </strong>
                        </td>
                        <td class="align-middle text-center">
                          <button
                            type="button"
                            class="btn btn-sm btn-danger"
                            @click="eliminarVolumenCalendario(sitioId, fecha)"
                            v-if="getVolumenCalendario(sitioId, fecha)"
                            :disabled="esEstadoDefinitivo"
                          >
                            <i class="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot class="bg-light">
                      <tr>
                        <th class="text-right">TOTALES:</th>
                        <th class="text-right">
                          {{
                            formatNumber(
                              getTotalVolumenCalendarioSitio(sitioId)
                            )
                          }}
                        </th>
                        <th class="text-right">
                          {{
                            formatNumber(
                              getTotalResultadoCalendarioSitio(sitioId)
                            )
                          }}
                        </th>
                        <th></th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="alert alert-info">
          <i class="fas fa-info-circle mr-2"></i>
          Seleccione un bloque y uno o más sitios para asignar volúmenes por
          fecha.
        </div>
      </div>
    </div>

    <!-- Botón de Guardar -->
    <div class="row mt-3" v-if="puedeGuardarCalendario">
      <div class="col-12 text-center">
        <button
          type="button"
          class="btn btn-success btn-lg"
          @click="saveVolumenesCalendario()"
          :disabled="esEstadoDefinitivo || cargando"
        >
          <i class="fas fa-save mr-2" v-if="!cargando"></i>
          <i class="fas fa-spinner fa-spin mr-2" v-else></i>
          {{ cargando ? "Guardando..." : "Guardar Volúmenes del Calendario" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "CalendarioVolumen",
  props: {
    fechaInicialMaestra: {
      type: String,
      default: "",
    },
    fechaFinalMaestra: {
      type: String,
      default: "",
    },
    bloques: {
      type: Array,
      default: () => [],
    },
    programacionId: {
      type: [Number, String],
      default: null,
    },
    estado: {
      type: [Number, String],
      default: 1,
    },
    detProgramacionesSitios: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      calendarioMes: new Date().getMonth(),
      calendarioAnio: new Date().getFullYear(),
      fechasSeleccionadas: [], // Array de strings de fechas en formato YYYY-MM-DD
      diasSemana: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
      meses: [
        "Enero",
        "Febrero",
        "Marzo",
        "Abril",
        "Mayo",
        "Junio",
        "Julio",
        "Agosto",
        "Septiembre",
        "Octubre",
        "Noviembre",
        "Diciembre",
      ],
      bloqueSeleccionado: null,
      sitiosSeleccionados: [], // Array de IDs de sitios seleccionados
      volumenesCalendario: [], // Array agrupado por sitio: [{ sitio_id, nombre, direccion, promedio_cargue, datos: [{ id, programacion_id, fecha, volumendia, cantidad_vh }] }]
      contadorId: 1, // Contador para generar IDs únicos
      cargando: false, // Estado de carga para el guardado
      datosCargados: false, // Flag para evitar cargar datos múltiples veces
    };
  },
  watch: {
    detProgramacionesSitios: {
      handler(newVal) {
        if (newVal && newVal.length > 0 && !this.datosCargados) {
          this.cargarDatosDesdeDetProgramacionesSitios();
        }
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    // Cargar datos si ya están disponibles
    if (
      this.detProgramacionesSitios &&
      this.detProgramacionesSitios.length > 0
    ) {
      this.cargarDatosDesdeDetProgramacionesSitios();
    }
  },
  computed: {
    esEstadoDefinitivo() {
      return this.estado == 2 || this.estado === "2";
    },

    puedeGuardarCalendario() {
      // Verificar que haya datos en volumenesCalendario
      if (!this.volumenesCalendario || this.volumenesCalendario.length === 0) {
        return false;
      }

      // Verificar que al menos un sitio tenga datos
      const tieneDatos = this.volumenesCalendario.some(
        (sitio) => sitio.datos && sitio.datos.length > 0
      );

      // Verificar que el programacionId exista
      if (!this.programacionId || this.programacionId <= 0) {
        return false;
      }

      // Verificar que todos los datos tengan el mismo programacionId
      const mismoProgramacionId = this.volumenesCalendario.every((sitio) => {
        if (!sitio.datos || sitio.datos.length === 0) return true;
        return sitio.datos.every(
          (dato) =>
            dato.programacion_id &&
            dato.programacion_id.toString() === this.programacionId.toString()
        );
      });

      return tieneDatos && mismoProgramacionId;
    },

    diasCalendario() {
      const primerDia = new Date(this.calendarioAnio, this.calendarioMes, 1);
      const ultimoDia = new Date(
        this.calendarioAnio,
        this.calendarioMes + 1,
        0
      );
      const diasEnMes = ultimoDia.getDate();
      const diaInicioSemana = primerDia.getDay();

      const dias = [];
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);

      // Obtener rango de fechas válidas (usar fechas maestras)
      const fechaInicialStr = this.fechaInicialMaestra;
      const fechaFinalStr = this.fechaFinalMaestra;

      let fechaInicial = null;
      let fechaFinal = null;
      if (fechaInicialStr && fechaFinalStr) {
        fechaInicial = new Date(fechaInicialStr + "T00:00:00");
        fechaInicial.setHours(0, 0, 0, 0);
        fechaFinal = new Date(fechaFinalStr + "T00:00:00");
        fechaFinal.setHours(0, 0, 0, 0);
      }

      // Función auxiliar para verificar si una fecha está en el rango
      const esFechaEnRango = (fecha) => {
        if (!fechaInicial || !fechaFinal) return false;
        const fechaComparar = new Date(fecha);
        fechaComparar.setHours(0, 0, 0, 0);
        return fechaComparar >= fechaInicial && fechaComparar <= fechaFinal;
      };

      // Días del mes anterior
      const mesAnterior = new Date(this.calendarioAnio, this.calendarioMes, 0);
      const diasMesAnterior = mesAnterior.getDate();
      for (let i = diaInicioSemana - 1; i >= 0; i--) {
        const fecha = new Date(
          this.calendarioAnio,
          this.calendarioMes - 1,
          diasMesAnterior - i
        );
        dias.push({
          numero: diasMesAnterior - i,
          fecha: fecha,
          esDelMes: false,
          seleccionado: false,
          esHoy: false,
          habilitado: esFechaEnRango(fecha),
        });
      }

      // Días del mes actual
      for (let i = 1; i <= diasEnMes; i++) {
        const fecha = new Date(this.calendarioAnio, this.calendarioMes, i);
        const fechaStr = this.formatearFechaParaComparar(fecha);
        const esHoy = fecha.getTime() === hoy.getTime();
        const seleccionado = this.fechasSeleccionadas.includes(fechaStr);
        const habilitado = esFechaEnRango(fecha);

        dias.push({
          numero: i,
          fecha: fecha,
          esDelMes: true,
          seleccionado: seleccionado,
          esHoy: esHoy,
          habilitado: habilitado,
        });
      }

      // Días del mes siguiente para completar la cuadrícula
      const diasRestantes = 42 - dias.length; // 6 semanas * 7 días
      for (let i = 1; i <= diasRestantes; i++) {
        const fecha = new Date(this.calendarioAnio, this.calendarioMes + 1, i);
        dias.push({
          numero: i,
          fecha: fecha,
          esDelMes: false,
          seleccionado: false,
          esHoy: false,
          habilitado: esFechaEnRango(fecha),
        });
      }

      return dias;
    },

    fechasSeleccionadasOrdenadas() {
      return [...this.fechasSeleccionadas].sort();
    },

    sitiosDelBloqueSeleccionado() {
      if (!this.bloqueSeleccionado || !this.bloques || !this.bloques.length) {
        return [];
      }
      const bloque = this.bloques.find((b) => b.id == this.bloqueSeleccionado);
      const sitios =
        bloque && Array.isArray(bloque.sitios) ? bloque.sitios : [];
      return sitios.filter((s) => s != null && s.id != null);
    },
  },
  methods: {
    getNombreMes(mes) {
      return this.meses[mes];
    },

    mesAnterior() {
      if (this.calendarioMes === 0) {
        this.calendarioMes = 11;
        this.calendarioAnio--;
      } else {
        this.calendarioMes--;
      }
    },

    mesSiguiente() {
      if (this.calendarioMes === 11) {
        this.calendarioMes = 0;
        this.calendarioAnio++;
      } else {
        this.calendarioMes++;
      }
    },

    toggleFecha(fecha) {
      if (!fecha) return;

      // Validar que la fecha esté dentro del rango
      if (!this.esFechaValida(fecha)) {
        this.$swal({
          icon: "warning",
          title: "Fecha fuera del rango",
          text:
            "Solo puede seleccionar fechas entre " +
            this.formatearFecha(this.fechaInicialMaestra) +
            " y " +
            this.formatearFecha(this.fechaFinalMaestra),
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      const fechaStr = this.formatearFechaParaComparar(fecha);
      const index = this.fechasSeleccionadas.indexOf(fechaStr);
      if (index > -1) {
        this.fechasSeleccionadas.splice(index, 1);
      } else {
        this.fechasSeleccionadas.push(fechaStr);
      }
    },

    esFechaValida(fecha) {
      if (!this.fechaInicialMaestra || !this.fechaFinalMaestra) {
        return false;
      }

      const fechaComparar = new Date(fecha);
      fechaComparar.setHours(0, 0, 0, 0);

      const fechaInicialDate = new Date(this.fechaInicialMaestra + "T00:00:00");
      fechaInicialDate.setHours(0, 0, 0, 0);

      const fechaFinalDate = new Date(this.fechaFinalMaestra + "T00:00:00");
      fechaFinalDate.setHours(0, 0, 0, 0);

      return (
        fechaComparar >= fechaInicialDate && fechaComparar <= fechaFinalDate
      );
    },

    eliminarFecha(fecha) {
      const index = this.fechasSeleccionadas.indexOf(fecha);
      if (index > -1) {
        this.fechasSeleccionadas.splice(index, 1);
      }
    },

    limpiarFechasSeleccionadas() {
      this.fechasSeleccionadas = [];
    },

    seleccionarTodosLosDiasHabilitados() {
      if (!this.fechaInicialMaestra || !this.fechaFinalMaestra) {
        this.$swal({
          icon: "warning",
          title: "Fechas no disponibles",
          text: "Debe establecer las fechas inicial y final en el formulario principal",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      // Crear fechas correctamente para evitar problemas de zona horaria
      const fechaInicial = new Date(this.fechaInicialMaestra + "T00:00:00");
      fechaInicial.setHours(0, 0, 0, 0);
      const fechaFinal = new Date(this.fechaFinalMaestra + "T00:00:00");
      fechaFinal.setHours(0, 0, 0, 0);

      // Limpiar fechas seleccionadas actuales
      this.fechasSeleccionadas = [];

      // Agregar todas las fechas del rango
      const fechaActual = new Date(fechaInicial);
      fechaActual.setHours(0, 0, 0, 0);

      while (fechaActual <= fechaFinal) {
        const fechaStr = this.formatearFechaParaComparar(fechaActual);
        this.fechasSeleccionadas.push(fechaStr);
        fechaActual.setDate(fechaActual.getDate() + 1);
      }

      this.$swal({
        icon: "success",
        title: "Días seleccionados",
        text: `Se seleccionaron ${this.fechasSeleccionadas.length} días del rango permitido`,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });
    },

    aplicarFechasCalendario() {
      if (this.fechasSeleccionadas.length === 0) {
        this.$swal({
          icon: "warning",
          title: "No hay fechas seleccionadas",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });
        return;
      }

      // Emitir evento para que el componente padre actualice las fechas
      const fechasOrdenadas = [...this.fechasSeleccionadas].sort();
      this.$emit("aplicar-fechas", {
        fechaInicial: fechasOrdenadas[0],
        fechaFinal: fechasOrdenadas[fechasOrdenadas.length - 1],
        totalDias: this.fechasSeleccionadas.length,
      });

      this.$swal({
        icon: "success",
        title: "Fechas aplicadas correctamente",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });
    },

    formatearFecha(fecha) {
      if (!fecha) return "";
      const date = new Date(fecha + "T00:00:00");
      const dia = date.getDate();
      const mes = date.getMonth() + 1;
      const anio = date.getFullYear();
      return `${dia.toString().padStart(2, "0")}/${mes
        .toString()
        .padStart(2, "0")}/${anio}`;
    },

    formatearFechaParaComparar(fecha) {
      if (!fecha) return "";
      if (typeof fecha === "string" && fecha.includes("-")) {
        return fecha;
      }
      const date = new Date(fecha);
      const anio = date.getFullYear();
      const mes = (date.getMonth() + 1).toString().padStart(2, "0");
      const dia = date.getDate().toString().padStart(2, "0");
      return `${anio}-${mes}-${dia}`;
    },

    // Métodos helper para volumenesCalendario agrupado por sitio
    obtenerSitioEnVolumenesCalendario(sitioId) {
      return this.volumenesCalendario.find((s) => s.sitio_id === sitioId);
    },

    crearSitioEnVolumenesCalendario(sitioId) {
      // Buscar información del sitio en los bloques
      let sitioInfo = null;
      for (const bloque of this.bloques) {
        const sitio = bloque.sitios.find((s) => s.id === sitioId);
        if (sitio) {
          sitioInfo = {
            sitio_id: sitioId,
            nombre: sitio.nombre || `Sitio #${sitioId}`,
            direccion: sitio.direccion || "",
            promedio_cargue:
              sitio.promedio_cargue || sitio.volumen || sitio.vol_sitio || 0,
            datos: [],
          };
          break;
        }
      }

      // Si no se encontró el sitio, crear uno básico
      if (!sitioInfo) {
        sitioInfo = {
          sitio_id: sitioId,
          nombre: `Sitio #${sitioId}`,
          direccion: "",
          promedio_cargue: 0,
          datos: [],
        };
      }

      this.volumenesCalendario.push(sitioInfo);
      return sitioInfo;
    },

    obtenerDatoPorFechaEnVolumenesCalendario(sitioId, fecha) {
      const sitio = this.obtenerSitioEnVolumenesCalendario(sitioId);
      if (!sitio || !sitio.datos) return null;
      return sitio.datos.find((dato) => dato.fecha === fecha);
    },

    // Métodos para volúmenes del calendario
    getVolumenCalendario(sitioId, fecha) {
      if (!sitioId || !fecha) return 0;
      const dato = this.obtenerDatoPorFechaEnVolumenesCalendario(
        sitioId,
        fecha
      );
      return dato ? dato.volumen_dia : 0;
    },

    setVolumenCalendario(sitioId, fecha, value) {
      if (!sitioId || !fecha || !this.programacionId) return;
      const volumendia = parseFloat(value) || 0;

      // Obtener o crear el sitio en volumenesCalendario
      let sitio = this.obtenerSitioEnVolumenesCalendario(sitioId);
      if (!sitio) {
        sitio = this.crearSitioEnVolumenesCalendario(sitioId);
      }

      // Buscar si ya existe un dato para esta fecha
      const datoExistente = this.obtenerDatoPorFechaEnVolumenesCalendario(
        sitioId,
        fecha
      );

      // Calcular cantidad_vh
      const volSitio = sitio.promedio_cargue || this.getVolSitio(sitioId);
      const cantidad_vh = volSitio > 0 ? volumendia / volSitio : 0;

      if (volumendia > 0) {
        const nuevoDato = {
          id: datoExistente ? datoExistente.id : this.contadorId++,
          programacion_id: this.programacionId,
          fecha: fecha,
          volumen_dia: volumendia,
          cantidad_vh: cantidad_vh,
        };

        if (datoExistente) {
          // Actualizar dato existente
          const indiceDato = sitio.datos.findIndex((d) => d.fecha === fecha);
          if (indiceDato >= 0) {
            this.$set(sitio.datos, indiceDato, nuevoDato);
          }
        } else {
          // Agregar nuevo dato
          if (!sitio.datos) {
            this.$set(sitio, "datos", []);
          }
          sitio.datos.push(nuevoDato);
        }
      } else {
        // Si el volumen es 0 o negativo, eliminar el dato si existe
        if (datoExistente && sitio.datos) {
          sitio.datos = sitio.datos.filter((d) => d.fecha !== fecha);

          // Si el sitio no tiene más datos, eliminarlo del array
          if (sitio.datos.length === 0) {
            const indiceSitio = this.volumenesCalendario.findIndex(
              (s) => s.sitio_id === sitioId
            );
            if (indiceSitio >= 0) {
              this.volumenesCalendario.splice(indiceSitio, 1);
            }
          }
        }
      }

      // Emitir evento para actualizar formVol en el componente padre
      this.$emit("actualizar-volumen-calendario", {
        sitioId,
        fecha,
        volumen: volumendia,
      });
    },

    eliminarVolumenCalendario(sitioId, fecha) {
      if (!sitioId || !fecha) return;

      const sitio = this.obtenerSitioEnVolumenesCalendario(sitioId);
      if (sitio && sitio.datos) {
        sitio.datos = sitio.datos.filter((d) => d.fecha !== fecha);

        // Si el sitio no tiene más datos, eliminarlo del array
        if (sitio.datos.length === 0) {
          const indiceSitio = this.volumenesCalendario.findIndex(
            (s) => s.sitio_id === sitioId
          );
          if (indiceSitio >= 0) {
            this.volumenesCalendario.splice(indiceSitio, 1);
          }
        }
      }

      // Emitir evento para eliminar del formVol en el componente padre
      this.$emit("eliminar-volumen-calendario", {
        sitioId,
        fecha,
      });
    },

    getVolSitio(sitioId) {
      for (const bloque of this.bloques) {
        const sitio = bloque.sitios.find((s) => s.id === sitioId);
        if (sitio) {
          return sitio.volumen || sitio.vol_sitio || sitio.promedio_cargue || 0;
        }
      }
      return 0;
    },

    getResultadoCalendario(sitioId, fecha) {
      const volumen = this.getVolumenCalendario(sitioId, fecha);
      const volSitio = this.getVolSitio(sitioId);
      return volSitio > 0 ? volumen / volSitio : 0;
    },

    obtenerNombreSitio(sitioId) {
      for (const bloque of this.bloques) {
        const sitio = bloque.sitios.find((s) => s.id === sitioId);
        if (sitio) {
          return sitio.nombre;
        }
      }
      return `Sitio #${sitioId}`;
    },

    getTotalVolumenCalendarioSitio(sitioId) {
      if (!sitioId) return 0;
      return this.fechasSeleccionadasOrdenadas.reduce((total, fecha) => {
        return total + this.getVolumenCalendario(sitioId, fecha);
      }, 0);
    },

    getTotalResultadoCalendarioSitio(sitioId) {
      if (!sitioId) return 0;
      return this.fechasSeleccionadasOrdenadas.reduce((total, fecha) => {
        return total + this.getResultadoCalendario(sitioId, fecha);
      }, 0);
    },

    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return "0.00";
      }
      return parseFloat(value)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    // Transformar volumenesCalendario al formato formVol que espera el backend
    transformarVolumenesCalendarioAFormVol() {
      const formVol = [];

      this.volumenesCalendario.forEach((sitio) => {
        if (sitio.datos && sitio.datos.length > 0) {
          // Transformar cada dato del calendario al formato esperado
          const datosTransformados = sitio.datos.map((dato) => ({
            fecha: dato.fecha,
            cantidad_vh: dato.cantidad_vh || 0,
            volumen_dia: dato.volumen_dia || 0,
            programacion_id: dato.programacion_id || this.programacionId,
            sitio_id: sitio.sitio_id,
            id: dato.id || this.contadorId++,
          }));

          formVol.push({
            programacion_id: this.programacionId,
            sitio_id: sitio.sitio_id,
            datos: datosTransformados,
          });
        }
      });

      return formVol;
    },

    // Cargar datos desde det_programaciones_sitios
    cargarDatosDesdeDetProgramacionesSitios() {
      if (
        !this.detProgramacionesSitios ||
        this.detProgramacionesSitios.length === 0
      ) {
        return;
      }

      // Agrupar datos por sitio_id
      const datosPorSitio = {};

      this.detProgramacionesSitios.forEach((dato) => {
        if (!dato.sitio_id || !dato.fecha) return;

        const sitioId = dato.sitio_id;

        if (!datosPorSitio[sitioId]) {
          datosPorSitio[sitioId] = {
            sitio_id: sitioId,
            datos: [],
          };
        }

        // Buscar información del sitio en los bloques
        let sitioInfo = null;
        for (const bloque of this.bloques) {
          const sitio = bloque.sitios.find((s) => s.id === sitioId);
          if (sitio) {
            sitioInfo = {
              nombre: sitio.nombre || `Sitio #${sitioId}`,
              direccion: sitio.direccion || "",
              promedio_cargue:
                sitio.promedio_cargue || sitio.volumen || sitio.vol_sitio || 0,
            };
            break;
          }
        }

        // Si no se encontró el sitio, crear uno básico
        if (!sitioInfo) {
          sitioInfo = {
            nombre: `Sitio #${sitioId}`,
            direccion: "",
            promedio_cargue: 0,
          };
        }

        // Agregar información del sitio si no está ya agregada
        if (!datosPorSitio[sitioId].nombre) {
          datosPorSitio[sitioId] = {
            ...datosPorSitio[sitioId],
            ...sitioInfo,
          };
        }

        // Transformar fecha al formato YYYY-MM-DD si es necesario
        let fechaStr = dato.fecha;
        if (fechaStr && fechaStr.includes("T")) {
          fechaStr = fechaStr.split("T")[0];
        }

        // Calcular cantidad_vh si no está presente
        let cantidad_vh = dato.cantidad_vh || dato.cargues || 0;
        if (!cantidad_vh && dato.volumen_dia && sitioInfo.promedio_cargue > 0) {
          cantidad_vh = dato.volumen_dia / sitioInfo.promedio_cargue;
        }

        // Agregar el dato al sitio
        datosPorSitio[sitioId].datos.push({
          id: dato.id || this.contadorId++,
          programacion_id: dato.programacion_id || this.programacionId,
          fecha: fechaStr,
          volumen_dia: dato.volumen_dia || 0,
          cantidad_vh: cantidad_vh,
        });
      });

      // Convertir el objeto a array y actualizar volumenesCalendario
      const sitiosArray = Object.values(datosPorSitio);

      // Agregar solo los sitios que no existen ya en volumenesCalendario
      sitiosArray.forEach((sitioNuevo) => {
        const sitioExistente = this.obtenerSitioEnVolumenesCalendario(
          sitioNuevo.sitio_id
        );

        if (sitioExistente) {
          // Si el sitio ya existe, agregar solo los datos nuevos que no existan
          sitioNuevo.datos.forEach((datoNuevo) => {
            const datoExistente = sitioExistente.datos.find(
              (d) => d.fecha === datoNuevo.fecha
            );
            if (!datoExistente) {
              sitioExistente.datos.push(datoNuevo);
            }
          });
        } else {
          // Si el sitio no existe, agregarlo completo
          this.volumenesCalendario.push(sitioNuevo);
        }
      });

      // Actualizar fechas seleccionadas con las fechas que tienen datos
      const fechasConDatos = new Set();
      sitiosArray.forEach((sitio) => {
        sitio.datos.forEach((dato) => {
          fechasConDatos.add(dato.fecha);
        });
      });

      // Agregar fechas que no estén ya seleccionadas
      fechasConDatos.forEach((fecha) => {
        if (!this.fechasSeleccionadas.includes(fecha)) {
          this.fechasSeleccionadas.push(fecha);
        }
      });

      // Seleccionar automáticamente los sitios que tienen datos cargados
      sitiosArray.forEach((sitio) => {
        if (!this.sitiosSeleccionados.includes(sitio.sitio_id)) {
          this.sitiosSeleccionados.push(sitio.sitio_id);
        }
      });

      // Si hay sitios con datos, seleccionar el bloque correspondiente del primer sitio
      if (sitiosArray.length > 0 && !this.bloqueSeleccionado) {
        const primerSitioId = sitiosArray[0].sitio_id;
        for (const bloque of this.bloques) {
          const sitio = bloque.sitios.find((s) => s.id === primerSitioId);
          if (sitio) {
            this.bloqueSeleccionado = bloque.id;
            break;
          }
        }
      }

      this.datosCargados = true;
    },

    // Función para guardar los volúmenes del calendario
    saveVolumenesCalendario() {
      if (!this.puedeGuardarCalendario) {
        this.$swal({
          icon: "warning",
          title: "No se puede guardar",
          text: "No hay datos válidos para guardar o falta información requerida",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      this.cargando = true;

      // Transformar los datos al formato que espera el backend
      const formVol = this.transformarVolumenesCalendarioAFormVol();

      axios
        .post("/api/argentina/programaciones/saveDetProgramacion", {
          formVol: formVol,
        })
        .then((response) => {
          this.cargando = false;
          this.$swal({
            title: "Se guardaron los volúmenes del calendario correctamente",
            icon: "success",
            confirmButtonText: "Ok!",
            timer: 3000,
            timerProgressBar: true,
          });
        })
        .catch((error) => {
          console.error("Error al guardar volúmenes del calendario:", error);
          this.$swal({
            title: "Ha ocurrido un error al guardar",
            text:
              error.response?.data?.message ||
              error.message ||
              "Error desconocido",
            icon: "error",
            confirmButtonText: "Ok!",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          this.cargando = false;
        });
    },

    duplicarVolumenes(sitioId) {
      if (!sitioId || this.fechasSeleccionadasOrdenadas.length === 0) return;

      // Obtener el volumen del primer día
      const primeraFecha = this.fechasSeleccionadasOrdenadas[0];
      const volumenPrimerDia = this.getVolumenCalendario(sitioId, primeraFecha);

      if (volumenPrimerDia <= 0) {
        this.$swal({
          icon: "warning",
          title: "No hay volumen para duplicar",
          text: "El primer día no tiene volumen asignado",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      // Duplicar el volumen a todas las fechas
      this.fechasSeleccionadasOrdenadas.forEach((fecha) => {
        this.setVolumenCalendario(sitioId, fecha, volumenPrimerDia);
      });

      this.$swal({
        icon: "success",
        title: "Volúmenes duplicados",
        text: `Se duplicó el volumen de ${this.formatNumber(
          volumenPrimerDia
        )} a todas las fechas`,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });
    },

    duplicarVolumenAFechas(sitioId, fechaOrigen) {
      if (
        !sitioId ||
        !fechaOrigen ||
        this.fechasSeleccionadasOrdenadas.length === 0
      )
        return;

      // Obtener el volumen de la fecha origen
      const volumen = this.getVolumenCalendario(sitioId, fechaOrigen);

      if (volumen <= 0) {
        this.$swal({
          icon: "warning",
          title: "No hay volumen para duplicar",
          text: "Esta fecha no tiene volumen asignado",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      // Duplicar el volumen a todas las fechas
      this.fechasSeleccionadasOrdenadas.forEach((fecha) => {
        this.setVolumenCalendario(sitioId, fecha, volumen);
      });

      this.$swal({
        icon: "success",
        title: "Volumen duplicado",
        text: `Se copió el volumen de ${this.formatNumber(
          volumen
        )} a todas las fechas`,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });
    },
  },
};
</script>
